/* Time, spelled out.
 *
 * Timezone is the single biggest gap on every station site we studied. A show
 * card that says "20:00" and does not say WHERE 20:00 is has told a listener in
 * Lisbon nothing, and told a listener in Chicago something false. So the zone
 * is a first-class argument here: every function that renders a moment takes a
 * TzMode, and there is no default that quietly means "the station's".
 *
 * TWO RULES, both learned the hard way:
 *
 * 1. Everything goes through Intl.DateTimeFormat with an EXPLICIT timeZone.
 *    Offset arithmetic is never hand-rolled. The last Sunday in March is
 *    exactly where hand-rolled arithmetic breaks, and it breaks silently: the
 *    schedule is an hour wrong for a week and nobody files a bug, they just
 *    miss a show.
 *
 * 2. The locale is pinned to en-GB, not the visitor's. That is a design
 *    decision, not an oversight — the whole system is 24-hour, day-before-
 *    month, uppercase mono. A US locale would hand back "8/23/2026" and
 *    "8:00 PM" into a layout built for "23 Aug" and "20:00", and the columns
 *    would not fit. The zone is the visitor's business; the notation is ours.
 *
 * A date that will not parse renders as an em dash rather than "Invalid Date".
 * The station is allowed to be missing a fact. It is not allowed to shout.
 */

/* -------------------------------------------------------------------- modes */

export const TZ_MODES = ['station', 'local', 'utc'] as const;

export type TzMode = (typeof TZ_MODES)[number];

/** The station's display zone. Mainland Spain, all year, DST included. */
export const STATION_TZ = 'Europe/Madrid';

/** Notation is ours; the zone is the visitor's. See the header. */
const LOCALE = 'en-GB';

/** What a field renders as when the value will not parse. */
const MISSING = '—';
const MISSING_CLOCK = '--:--:--';

/** localStorage key for the visitor's choice, shared by every surface. */
export const TZ_STORAGE_KEY = 'hgr.tz';

export function isTzMode(value: unknown): value is TzMode {
  return typeof value === 'string' && (TZ_MODES as readonly string[]).includes(value);
}

/**
 * The visitor's saved preference, or the station's zone.
 *
 * Station time is the default on purpose: it is the only one of the three that
 * is a fact about the broadcast rather than a fact about the reader, so it is
 * the right thing to show someone who has not chosen yet.
 */
export function readTzMode(): TzMode {
  try {
    const saved = window.localStorage.getItem(TZ_STORAGE_KEY);
    return isTzMode(saved) ? saved : 'station';
  } catch {
    // Private mode, or storage disabled. Not worth a broken page.
    return 'station';
  }
}

export function writeTzMode(mode: TzMode): void {
  try {
    window.localStorage.setItem(TZ_STORAGE_KEY, mode);
  } catch {
    /* nothing to do, and nothing worth saying */
  }
}

/** The IANA zone a mode resolves to. */
export function resolveTz(mode: TzMode): string {
  if (mode === 'utc') return 'UTC';
  if (mode === 'local') return localTz();
  return STATION_TZ;
}

function localTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || STATION_TZ;
  } catch {
    return STATION_TZ;
  }
}

/* ------------------------------------------------------------------ parsing */

export type DateInput = string | number | Date;

interface Parsed {
  at: Date;
  /**
   * True for a bare `YYYY-MM-DD`, which is a CALENDAR DATE and not a moment.
   * `schedule.days[].date` is one of these. Rendering it in the reader's zone
   * is how "Mon 24 Aug" becomes "Sun 23 Aug" for anyone west of Greenwich —
   * the day a station calls Monday is Monday everywhere.
   */
  dateOnly: boolean;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function parse(input: DateInput | null | undefined): Parsed | null {
  if (input === null || input === undefined) return null;

  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : { at: input, dateOnly: false };
  }
  if (typeof input === 'number') {
    const at = new Date(input);
    return Number.isNaN(at.getTime()) ? null : { at, dateOnly: false };
  }

  const text = input.trim();
  if (!text) return null;

  const dateOnly = DATE_ONLY.test(text);
  // A bare date parses as UTC midnight, which is what we want: it is then
  // formatted back in UTC and the calendar date survives the round trip.
  const at = new Date(dateOnly ? `${text}T00:00:00Z` : text);
  return Number.isNaN(at.getTime()) ? null : { at, dateOnly };
}

/* ---------------------------------------------------------------- formatters
 * Intl.DateTimeFormat construction is the expensive part, and a schedule page
 * formats a few hundred timestamps. Built once per (zone, shape) and kept. */

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string, key: string, options: Intl.DateTimeFormatOptions) {
  const cacheKey = `${timeZone}|${key}`;
  let found = formatters.get(cacheKey);
  if (!found) {
    found = new Intl.DateTimeFormat(LOCALE, { ...options, timeZone });
    formatters.set(cacheKey, found);
  }
  return found;
}

/** The zone a value should actually be rendered in, honouring the date-only rule. */
function zoneFor(parsed: Parsed, mode: TzMode): string {
  return parsed.dateOnly ? 'UTC' : resolveTz(mode);
}

function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? '';
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/* --------------------------------------------------------------------- time */

/** `20:00`. Always 24-hour: h23, so midnight is 00:00 and never 24:00. */
export function formatTime(iso: DateInput | null | undefined, mode: TzMode): string {
  const parsed = parse(iso);
  if (!parsed) return MISSING;
  return formatter(zoneFor(parsed, mode), 'time', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(parsed.at);
}

/**
 * `Sun 23 Aug`. Composed from parts rather than taken raw, because en-GB puts
 * a comma after the weekday and the design does not have one.
 */
export function formatDayDate(iso: DateInput | null | undefined, mode: TzMode): string {
  const parsed = parse(iso);
  if (!parsed) return MISSING;
  const parts = formatter(zoneFor(parsed, mode), 'daydate', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).formatToParts(parsed.at);
  return `${part(parts, 'weekday')} ${part(parts, 'day')} ${part(parts, 'month')}`.trim();
}

/** `26 July 2026`. The long form, for a body paragraph or an episode header. */
export function formatFullDate(iso: DateInput | null | undefined, mode: TzMode): string {
  const parsed = parse(iso);
  if (!parsed) return MISSING;
  return formatter(zoneFor(parsed, mode), 'fulldate', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(parsed.at);
}

/**
 * `20:00 — 22:00`. An em dash with spaces, as drawn.
 *
 * Both ends are formatted independently in the same zone, so a show that runs
 * across the spring-forward boundary reads 01:00 — 03:00 rather than being
 * quietly shortened by an hour.
 */
export function formatTimeRange(
  a: DateInput | null | undefined,
  b: DateInput | null | undefined,
  mode: TzMode,
): string {
  return `${formatTime(a, mode)} — ${formatTime(b, mode)}`;
}

/* ---------------------------------------------------------------- durations */

/**
 * `119 MIN`. Minutes, never hours-and-minutes: the archive is a wall of these
 * and one unit reads as a column. Anything above zero is at least a minute,
 * because "0 MIN" next to a set that exists is a bug on its face.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return '0 MIN';
  return `${Math.max(1, Math.round(seconds / 60))} MIN`;
}

/**
 * `48:32`, or `1:59:12` once it passes an hour. Elapsed time on a playing set:
 * the hour field appears only when there is an hour to show, so the field does
 * not sit there as `00:` for the first hour of every broadcast.
 */
export function formatClock(seconds: number | null | undefined): string {
  const total =
    typeof seconds === 'number' && Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
}

/**
 * `07:55:41` — the off-air counter for the next show.
 *
 * Zero-padded and fixed-width so the digits do not jitter as they tick. Hours
 * are not capped at 24: a booking three days out honestly reads 76:12:09, and
 * a number that large is itself the information ("nothing tonight").
 *
 * `from` exists so the counter can be run against `server_time` out of
 * /api/live rather than against a laptop clock that is four minutes fast.
 */
export function countdown(iso: DateInput | null | undefined, from?: DateInput): string {
  const target = parse(iso);
  if (!target) return MISSING_CLOCK;

  const start = from === undefined ? new Date() : parse(from)?.at;
  if (!start) return MISSING_CLOCK;

  const seconds = Math.floor((target.at.getTime() - start.getTime()) / 1000);
  if (seconds <= 0) return '00:00:00';

  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(
    seconds % 60,
  )}`;
}

/* ------------------------------------------------------------------- labels */

/**
 * `Madrid · CEST`. The abbreviation is asked of Intl rather than stored, so
 * it says CET in January and CEST in July without anyone remembering to change
 * it. A zone with no abbreviation in the CLDR data answers `GMT+9`, which is
 * less pretty and equally true.
 */
export function tzLabel(mode: TzMode, at?: DateInput): string {
  const zone = resolveTz(mode);
  if (zone === 'UTC') return 'UTC';

  const when = parse(at ?? new Date())?.at ?? new Date();
  const abbreviation = part(
    formatter(zone, 'tzname', { timeZoneName: 'short' }).formatToParts(when),
    'timeZoneName',
  );

  const city = (zone.split('/').pop() ?? zone).replace(/_/g, ' ');
  if (!abbreviation || abbreviation === city) return city;
  return `${city} · ${abbreviation}`;
}

/**
 * The caption for the three-way control in the design: the station's zone is
 * named, the visitor's is not (we would be telling them something they already
 * know), and UTC is UTC.
 */
export function tzModeLabel(mode: TzMode): string {
  if (mode === 'utc') return 'UTC';
  if (mode === 'local') return 'My time zone';
  return tzLabel('station');
}

/* -------------------------------------------------------- calendar helpers */

/** `2026-08-24` — the form `/schedule/:date` and `?from=` are written in. */
export function isoDate(d: DateInput | null | undefined, mode: TzMode): string {
  const parsed = parse(d);
  if (!parsed) return '';
  const parts = formatter(zoneFor(parsed, mode), 'isodate', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(parsed.at);
  return `${part(parts, 'year')}-${part(parts, 'month')}-${part(parts, 'day')}`;
}

/**
 * Calendar arithmetic on a bare `YYYY-MM-DD`, for the schedule's week arrows.
 *
 * Done in UTC and on a date-only value, so it is pure day counting and cannot
 * land on 23:00 the previous evening the weekend the clocks move.
 */
export function shiftIsoDate(date: string, days: number): string {
  const parsed = parse(date);
  if (!parsed) return date;
  const shifted = new Date(parsed.at.getTime());
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

/** Today, as the station reckons it. The schedule's default `from`. */
export function stationToday(): string {
  return isoDate(new Date(), 'station');
}
